import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Bed,
  Bath,
  Square,
  DollarSign,
  User,
  Calendar,
  Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SplitReportListingDetail() {
  const { listingId } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => { fetchListing(); }, [listingId]);

  const fetchListing = async () => {
    try {
      const res = await fetch(`${API}/split-report/listings/${listingId}`, { credentials: 'include' });
      if (res.ok) setListing(await res.json());
      else toast.error('Listing not found');
    } catch { toast.error('Failed to load listing'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API}/split-report/listings/${listingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const updated = await res.json();
        setListing(updated);
        toast.success(`Status updated to ${newStatus}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update'); }
    finally { setUpdatingStatus(false); }
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  const formatAmount = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  const statusColors = { Active: 'bg-green-100 text-green-700 border-green-200', Pending: 'bg-amber-100 text-amber-700 border-amber-200', Sold: 'bg-blue-100 text-blue-700 border-blue-200' };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Listing Not Found</h2>
            <Link to="/split-report"><Button className="bg-[#3D4F7A] hover:bg-[#2A3755]">Back to Split Report</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwner = listing.agent_id === user?.user_id;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="listing-detail">
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/split-report/listings" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{listing.street_address}</h1>
                <p className="text-slate-300">{listing.city}, {listing.state} {listing.zip_code}</p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="sr-dashboard-btn">
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="md:col-span-2 space-y-6">
            {/* Property Image */}
            {listing.property_image ? (
              <div className="rounded-2xl overflow-hidden h-64 bg-slate-100">
                <img src={listing.property_image} alt={listing.street_address} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="rounded-2xl h-64 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <Building2 className="w-16 h-16 text-slate-300" />
              </div>
            )}

            {/* Price & Status */}
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{formatPrice(listing.sales_price)}</p>
                    <span className={`inline-block mt-1 px-3 py-0.5 rounded-full text-sm font-semibold border ${statusColors[listing.status]}`}>
                      {listing.status}
                    </span>
                  </div>
                  {isOwner && (
                    <div className="flex gap-2">
                      {listing.status !== 'Active' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus('Active')} disabled={updatingStatus}>Set Active</Button>
                      )}
                      {listing.status !== 'Pending' && (
                        <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" onClick={() => updateStatus('Pending')} disabled={updatingStatus}>Set Pending</Button>
                      )}
                      {listing.status !== 'Sold' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => updateStatus('Sold')} disabled={updatingStatus}>Mark Sold</Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-6 text-slate-600">
                  <span className="flex items-center gap-1.5"><Bed className="w-4 h-4" />{listing.bedrooms} Bedrooms</span>
                  <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" />{listing.bathrooms} Bathrooms</span>
                  <span className="flex items-center gap-1.5"><Square className="w-4 h-4" />{listing.square_feet?.toLocaleString()} sqft</span>
                </div>
              </CardContent>
            </Card>

            {/* Commission Splits */}
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-4">
                  <Percent className="w-5 h-5 text-teal-500" />Commission Splits
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 rounded-xl bg-blue-50 border border-blue-100">
                    <p className="text-xs text-slate-500 font-medium mb-1">Buyer's Agent</p>
                    <p className="text-2xl font-bold text-blue-700">{listing.commission_mode === 'dollar' ? formatAmount(listing.buyer_agent_commission) : `${listing.buyer_agent_commission}%`}</p>
                    <p className="text-sm font-semibold text-green-600 mt-1">{listing.commission_mode === 'dollar' ? `${((listing.buyer_agent_commission / listing.sales_price) * 100).toFixed(2)}%` : formatAmount(listing.buyer_agent_amount)}</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-purple-50 border border-purple-100">
                    <p className="text-xs text-slate-500 font-medium mb-1">Sub-Agent</p>
                    <p className="text-2xl font-bold text-purple-700">{listing.commission_mode === 'dollar' ? formatAmount(listing.sub_agent_commission) : `${listing.sub_agent_commission}%`}</p>
                    <p className="text-sm font-semibold text-green-600 mt-1">{listing.commission_mode === 'dollar' ? `${((listing.sub_agent_commission / listing.sales_price) * 100).toFixed(2)}%` : formatAmount(listing.sub_agent_amount)}</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <p className="text-xs text-slate-500 font-medium mb-1">TC</p>
                    <p className="text-2xl font-bold text-amber-700">{listing.commission_mode === 'dollar' ? formatAmount(listing.tc_commission) : `${listing.tc_commission}%`}</p>
                    <p className="text-sm font-semibold text-green-600 mt-1">{listing.commission_mode === 'dollar' ? `${((listing.tc_commission / listing.sales_price) * 100).toFixed(2)}%` : formatAmount(listing.tc_amount)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {listing.notes && (
              <Card className="border border-slate-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Notes</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{listing.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Listing Agent</h3>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#3D4F7A] flex items-center justify-center text-white font-bold">
                    {listing.agent_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{listing.agent_name}</p>
                    {listing.agent_member_id && <p className="text-xs text-slate-500">AREA #{listing.agent_member_id}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Listed</span><span className="font-medium">{formatDate(listing.created_at)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Updated</span><span className="font-medium">{formatDate(listing.updated_at)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Listing ID</span><span className="font-mono text-xs">{listing.listing_id}</span></div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 bg-slate-50">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Commission data is shared for transparency purposes. Listings cannot be deleted to maintain a permanent record.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
